/**
 * BaseSelect - Foundational select component with styling
 *
 * This is a low-level component. Prefer using SelectField.
 */

import { ChevronDown } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from '../utils';

export interface BaseSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Whether the select has an error state */
  hasError?: boolean;
}

export const BaseSelect = forwardRef<HTMLSelectElement, BaseSelectProps>(
  ({ hasError, className, children, ...props }, ref) => {
    return (
      <div className="relative">
        <select
          ref={ref}
          className={cn(
            'w-full appearance-none rounded-lg border px-4 py-2.5 pr-10 text-sm transition-all',
            'bg-gray-50 text-gray-900 dark:bg-[#18181b] dark:text-white',
            'focus:outline-none focus:ring-1',
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 dark:border-white/[0.06]',
            props.disabled && 'cursor-not-allowed opacity-50',
            className
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-zinc-500" />
      </div>
    );
  }
);

BaseSelect.displayName = 'BaseSelect';
